import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { User } from '../models/User';
import { Establishment } from '../models/Establishment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userSource: BehaviorSubject<any> = new BehaviorSubject<User | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private cookie: CookieService
  ) {
    this.setUser(this.user);
  }

  setUser(value: User | null) {
    this.userSource.next(value);
  }

  getUser(): Observable<User> {
    return this.userSource.asObservable();
  }

  get token(): string {
    try {
      return this.cookie.get('token');
    } catch (error) {
      return '';
    }
  }

  /** Unix timestamp (seconds) when the JWT expires, stored at login. */
  get tokenExpiry(): number | null {
    const raw = localStorage.getItem('token_expiry');
    const expiry = raw ? Number(raw) : NaN;
    return Number.isFinite(expiry) ? expiry : null;
  }

  /** True when there is no token, or the stored expiry is in the past. */
  isTokenExpired(): boolean {
    if (!this.token) {
      return true;
    }
    const expiry = this.tokenExpiry;
    return expiry !== null && Date.now() / 1000 >= expiry;
  }

  get user(): User | null {
    try {
      return JSON.parse(localStorage.getItem('user')!);
    } catch {
      return null;
    }
  }
  get establishment(): Establishment | null {
    try {
      // @ts-ignore
      return JSON.parse(localStorage.getItem('temp_establishment'));
    } catch {
      return null;
    }
  }

  get id(): number | null {
    try {
      return Number(localStorage.getItem('temp_id'));
    } catch (error) {
      return null;
    }
  }

  /**
   * The agency the connected agency account acts for. A collaborator (AGENCY_USER) acts on behalf
   * of its agency, so this is `agency_id` — NOT its personal user id. For the legacy AGENCY account
   * both are equal.
   */
  get agencyId(): number | null {
    const u: any = this.user;
    if (!u) {
      return null;
    }
    const agencyId = Number(u.agency_id);
    return Number.isFinite(agencyId) && agencyId > 0 ? agencyId : u.id ?? null;
  }

  /** Display name of the connected user ("Prénom Nom", falling back to the email). */
  get displayName(): string {
    const u: any = this.user;
    if (!u) {
      return '';
    }
    const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
    return full || u.name || u.email || '';
  }

  // ─────────────── 2FA (agency accounts) ───────────────
  verifyTwoFactor(challengeId: string, code: string): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/2fa/verify`, { challenge_id: challengeId, code });
  }

  resendTwoFactor(challengeId: string): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/2fa/resend`, { challenge_id: challengeId });
  }

  // ─────────────── collaborator invitations (public) ───────────────
  getInvitation(token: string): Observable<any> {
    return this.http.get(`${environment.api.BASE_URL}/auth/invitations`, { params: { token } });
  }

  acceptInvitation(token: string, password: string, passwordConfirmation: string): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/invitations/accept`, {
      token,
      password,
      passwordConfirmation,
    });
  }

  /**
   * Reloads the connected user (role, permissions, status) from the server so a role change made
   * by an admin is reflected in the UI without a new login. The backend already applies it on the
   * very next request.
   */
  refreshCurrentUser(): Observable<any> {
    return new Observable((observer) => {
      if (!this.user || this.isTokenExpired()) {
        observer.next(null);
        observer.complete();
        return;
      }
      this.http.get<any>(`${environment.api.BASE_URL}/users/me`).subscribe({
        next: (fresh) => {
          if (fresh && fresh.id) {
            const merged = { ...(this.user as any), ...fresh };
            localStorage.setItem('user', JSON.stringify(merged));
            // Agency screens read `id` as the AGENCY id (sessions opened before the multi-user
            // release stored the personal id there).
            if (merged.kind === 'AGENCY' || merged.kind === 'AGENCY_USER') {
              const agencyId = Number(merged.agency_id) > 0 ? Number(merged.agency_id) : merged.id;
              localStorage.setItem('id', String(agencyId));
            }
            this.setUser(merged);
          }
          observer.next(fresh);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }
  notif() {
    return this.http.get(`${environment.api.BASE_URL}/agencies/notifications`);
  }
  readNotif(id: number, body: any) {
    return this.http.put(
      `${environment.api.BASE_URL}/notifications/${id}/read`,
      body
    );
  }
  login(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/sign_in`, body);
  }
  fogetPassword(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/password/forgot`, body);
  }

  validateSiret(siret: string): Observable<any> {
    return this.http.get(`${environment.api.SIRET_API_URL}/${siret}`);
  }

  signupAgency(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/sign_up`, body);
  }

  // One-time agency onboarding for a fresh instance (creates the agency + its
  // AGENCY / AGENCY_USER accounts). Public endpoint.
  onboard(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}/auth/onboard`, body);
  }

  // Public: whether the instance already has an agency (AGENCY user) registered.
  // Used to hide the registration CTA and block the /agency/register route.
  onboardingStatus(): Observable<any> {
    return this.http.get(`${environment.api.BASE_URL}/auth/onboarding-status`);
  }

  signup(body: any): Observable<any> {
    return this.http.post(`${environment.api.BASE_URL}`, body);
  }

  activate(email: string, code: string): Observable<any> {
    return this.http.get(`${environment.api.BASE_URL}/auth/check_verification_code?email=${email}&code=${code}`);
  }

  disconnect() {
    localStorage.clear();
    this.cookie.delete('token');
    this.cookie.delete('token', '/');
    this.setUser(null);
    this.router.navigate(['/login']);
  }
}
